/**
 * Helpers for matches stored on line items (where an item was found) and for the plan fields that merely
 * summarise the line items.
 *
 * Items are the run's `item_results`: each has a status, optional `matches` (one per support file, with
 * pages/rows) and, for older runs, only `matched_to` / `citation`.
 */

const isMatch = (m) => m && (m.status === 'matched' || m.status === 'combined');

/** A file name the AI wrote when there was no file ("none", "n/a"): not a file. */
export const isRealFile = (name) => !!name && !/^\s*(none|n\/a|na|null|unknown|-|—)\s*$/i.test(String(name));

/** Where an item was found: [{ file, pages: [1,2], rows: [3], combined }] — from `matches`, else `matched_to`. */
// "1-3, 5" → [1, 2, 3, 5]
export function parsePages(text) {
  const pages = [];
  String(text || '').split(/[,\s]+/).forEach((part) => {
    const range = part.match(/^(\d+)-(\d+)$/);
    if (range) {
      const [a, b] = [Number(range[1]), Number(range[2])];
      for (let p = Math.min(a, b); p <= Math.max(a, b) && p - Math.min(a, b) < 200; p += 1) pages.push(p);
    } else if (Number(part)) pages.push(Number(part));
  });
  return [...new Set(pages)].sort((a, b) => a - b);
}

export function itemMatchPlaces(item) {
  // The pages the line was matched on in its cited file (the AI's and the matcher's, joined) belong to the place
  // for that file, so a receipt page next to the invoice is not left out.
  const citedFile = (item?.citation?.source_file || '').toLowerCase();
  const fromMatches = (item?.matches || []).filter(isMatch).map((m) => ({
    file: m.file,
    pages: [...new Set([
      ...(m.locations || []).map((l) => l.page_number).filter((p) => p != null),
      ...(citedFile && m.file?.toLowerCase() === citedFile ? (item.matched_pages || []) : []),
    ])].sort((a, b) => a - b),
    rows: [...new Set((m.locations || []).filter((l) => l.page_number == null).map((l) => l.row_number).filter((r) => r != null))].sort((a, b) => a - b),
    combined: m.status === 'combined',
  }));
  if (fromMatches.length) return fromMatches;
  const to = (item?.matched_to || '').trim();
  if (to) {
    const m = to.match(/^(.*?)\s+p([\d, -]+)$/i);
    const file = (m ? m[1] : to).trim();
    if (isRealFile(file)) return [{ file, pages: m ? parsePages(m[2]) : [], rows: [], combined: false }];
  }
  const cited = item?.citation?.source_file;
  return isRealFile(cited) ? [{ file: cited, pages: item.citation.page_number ? [item.citation.page_number] : [], rows: [], combined: false }] : [];
}

/** "Invoices.pdf p1, 2" / "log.xlsx row 4" */
export function formatPlace(p) {
  const bits = [];
  if (p.pages.length) bits.push(`p${p.pages.join(', ')}`);
  if (p.rows.length) bits.push(`row ${p.rows.join(', ')}`);
  return `${p.file}${bits.length ? ` ${bits.join(' ')}` : ''}`;
}

const RULES = [
  // order matters: the more specific wording first ("All Line Items Have Supporting Documentation" also says "line items")
  { test: /exception|unsupported/ },
  { test: /all[ _]line[ _]items|every[ _]line|all[ _]items[ _]sup|all[ _]sup/ },
  { test: /matched/ },
  { test: /attached|supporting[ _](invoice|doc)/ },
  { test: /(number|count|total)[ _]of[ _]line|line[ _]?item[ _]?count|line[ _]items?[ _]on/ },
];

const FIELD_KIND = [
  ['amount', /\b(amount|amt|value|total)\b/],
  ['date', /\bdate\b/],
  ['party', /\b(vendor|merchant|payee|supplier|customer|description)\b/],
];
const SOURCE_SIDE = /\b(statement|transaction|ledger|bank|line)\b/;
const SUPPORT_SIDE = /\b(invoice|receipt|bill|support|supporting)\b/;

/**
 * Plan fields that describe ONE transaction (statement amount, invoice amount, transaction date, vendor name) have one
 * extracted value per sample. When a sample holds several line items that single value belongs to just one of them, so
 * repeating it on every row is wrong. For those fields each row shows its own line's value instead:
 *   statement side -> the line as read from the source list; invoice side -> what the matched document shows (the amount).
 * Whatever a line cannot answer stays empty. Returns null for fields that are not about one transaction (month, reviewer,
 * card number ...), whose sample value is right on every row.
 */
export function lineLevelFieldValue(planField, item) {
  const key = `${planField?.field_key || ''} ${planField?.label || ''} ${planField?._liveLabel || ''}`.toLowerCase().replace(/_/g, ' ');
  const kind = (FIELD_KIND.find(([, re]) => re.test(key)) || [])[0];
  const side = SUPPORT_SIDE.test(key) ? 'support' : SOURCE_SIDE.test(key) ? 'source' : null;
  if (!kind || !side) return null;
  const src = item?.source || {};
  if (side === 'source') {
    if (kind === 'amount') return src.amount ? formatMoney(src.amount) : '';
    if (kind === 'date') return src.date || '';
    return src.party || '';
  }
  if (kind === 'amount') {
    const m = (item?.matches || []).find((x) => x && (x.status === 'matched' || x.status === 'combined'));
    return m?.found_amount ? formatMoney(m.found_amount) : '';
  }
  return '';
}

const formatMoney = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : String(v);
};

/** True for plan fields that summarise the line items (count, matched, all supported, exceptions). In the
 *  by-line-item table each line carries that answer itself, so these are not repeated as sample columns. */
export function isLineItemAggregateField(planField) {
  const key = `${planField?.field_key || ''} ${planField?.label || ''} ${planField?._liveLabel || ''}`.toLowerCase();
  return RULES.some((r) => r.test.test(key));
}
